/**
 * Spritesheets (spec 033): the cut (how a media item's image divides into
 * frames, `SheetCut`, stored on the media item) and a state's playback
 * (`StatePlayback`, stored on the sprite state). Pure: no clock, no DOM.
 * Every renderer takes its frame from `sheetFrameAt` through
 * `evaluateFrame`, so seeking is exact and all of them agree.
 */

import type { SheetCut, SheetRect, SpriteSize, StatePlayback } from '../@types';

export const SHEET_MAX_CELLS = 64;
export const SHEET_FPS_MIN = 1;
export const SHEET_FPS_MAX = 60;
export const PLAYBACK_SPEED_MIN = 0.25;
export const PLAYBACK_SPEED_MAX = 3;

/** A state with no `playback` whose picture has a cut plays this. */
export const DEFAULT_PLAYBACK: StatePlayback = Object.freeze({ mode: 'loop' }) as StatePlayback;

const CUT_KEYS = ['columns', 'rows', 'frameCount', 'fps', 'cutBy', 'json', 'pixelArt', 'source'];
const PLAYBACK_KEYS = ['speed', 'mode', 'range', 'whenDone', 'randomStart'];
const MODES = ['loop', 'once', 'pingpong', 'still'];

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const isInt = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const clampInt = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Math.round(v)));

/**
 * Why `cut` is not a valid `SheetCut` (a reason naming the field), or null
 * when it is. Unknown keys are refused.
 */
export function validateSheetCut(cut: unknown): string | null {
  if (!isObject(cut)) return 'sheet must be an object';
  for (const key of Object.keys(cut)) if (!CUT_KEYS.includes(key)) return `sheet.${key} is not a known field`;
  const { columns, rows, frameCount, fps, cutBy, json, pixelArt, source } = cut;
  if (!isInt(columns) || columns < 1 || columns > SHEET_MAX_CELLS) return `sheet.columns must be a whole number from 1 to ${SHEET_MAX_CELLS}`;
  if (!isInt(rows) || rows < 1 || rows > SHEET_MAX_CELLS) return `sheet.rows must be a whole number from 1 to ${SHEET_MAX_CELLS}`;
  if (!isInt(frameCount) || frameCount < 1 || frameCount > columns * rows) {
    return `sheet.frameCount must be a whole number from 1 to columns × rows (${columns * rows})`;
  }
  if (!isNum(fps) || fps < SHEET_FPS_MIN || fps > SHEET_FPS_MAX) return `sheet.fps must be from ${SHEET_FPS_MIN} to ${SHEET_FPS_MAX}`;
  if (cutBy !== 'grid' && cutBy !== 'json') return "sheet.cutBy must be 'grid' or 'json'";
  if (json !== undefined) {
    if (!isObject(json)) return 'sheet.json must be an object';
    for (const key of Object.keys(json)) if (key !== 'frames' && key !== 'frameMs') return `sheet.json.${key} is not a known field`;
    if (!Array.isArray(json.frames)) return 'sheet.json.frames must be an array';
    for (let i = 0; i < json.frames.length; i++) {
      const r = json.frames[i];
      if (!isObject(r) || !isNum(r.x) || !isNum(r.y) || !isNum(r.w) || !isNum(r.h) || r.x < 0 || r.y < 0 || !(r.w > 0) || !(r.h > 0)) {
        return `sheet.json.frames[${i}] must be { x, y, w, h } with x, y ≥ 0 and w, h > 0`;
      }
    }
    if (json.frameMs !== undefined) {
      if (!Array.isArray(json.frameMs)) return 'sheet.json.frameMs must be an array';
      if (json.frameMs.length !== json.frames.length) return 'sheet.json.frameMs must have one duration per frame of sheet.json.frames';
      for (let i = 0; i < json.frameMs.length; i++) {
        if (!isNum(json.frameMs[i]) || (json.frameMs[i] as number) < 1) return `sheet.json.frameMs[${i}] must be at least 1 ms`;
      }
    }
  }
  if (cutBy === 'json') {
    const frames = isObject(json) && Array.isArray(json.frames) ? json.frames.length : 0;
    if (frames !== frameCount) return `sheet.json.frames must have frameCount (${frameCount}) rects when sheet.cutBy is 'json'`;
  }
  if (pixelArt !== undefined && typeof pixelArt !== 'boolean') return 'sheet.pixelArt must be true or false';
  if (source !== undefined && source !== 'json' && source !== 'grid' && source !== 'frames') return "sheet.source must be 'json', 'grid' or 'frames'";
  return null;
}

/**
 * Why `playback` is not a valid `StatePlayback` (a reason naming the field),
 * or null when it is. Playback says only how a state moves (D-7), so a stored
 * `loops` (an unknown key) and a `whenDone: { goTo }` are refused by name.
 * `cut`, when known, also bounds `range` (without it the runtime clamps instead).
 */
export function validateStatePlayback(playback: unknown, cut?: SheetCut): string | null {
  if (!isObject(playback)) return 'playback must be an object';
  for (const key of Object.keys(playback)) if (!PLAYBACK_KEYS.includes(key)) return `playback.${key} is not a known field`;
  const { speed, mode, range, whenDone, randomStart } = playback;
  if (speed !== undefined && (!isNum(speed) || speed < PLAYBACK_SPEED_MIN || speed > PLAYBACK_SPEED_MAX)) {
    return `playback.speed must be from ${PLAYBACK_SPEED_MIN} to ${PLAYBACK_SPEED_MAX}`;
  }
  if (typeof mode !== 'string' || !MODES.includes(mode)) return `playback.mode must be one of ${MODES.join(', ')}`;
  if (range !== undefined) {
    if (!Array.isArray(range) || range.length !== 2 || !isInt(range[0]) || !isInt(range[1]) || range[0] < 0 || range[1] < 0) {
      return 'playback.range must be [first, last], two whole frame indexes from 0';
    }
    if (range[0] > range[1]) return 'playback.range must not be reversed (first ≤ last)';
    if (cut && range[1] > cut.frameCount - 1) return `playback.range must lie within 0 … ${cut.frameCount - 1}`;
  }
  if (whenDone !== undefined && whenDone !== 'hold-last' && whenDone !== 'hold-first') {
    return "playback.whenDone must be 'hold-last' or 'hold-first'";
  }
  if (randomStart !== undefined && typeof randomStart !== 'boolean') return 'playback.randomStart must be true or false';
  return null;
}

/**
 * The scene validators' check (spec 033): every state's `playback`, as
 * `validateStatePlayback`; the reason carries the path
 * (`sprites[0].states[1].playback.speed …`). Null when all are valid.
 */
export function validateSpritesPlayback(sprites: unknown): string | null {
  if (!Array.isArray(sprites)) return null;
  for (let i = 0; i < sprites.length; i++) {
    const sprite = sprites[i];
    if (!isObject(sprite) || !Array.isArray(sprite.states)) continue;
    const states = sprite.states as unknown[];
    for (let j = 0; j < states.length; j++) {
      const state = states[j];
      if (!isObject(state) || state.playback === undefined) continue;
      const reason = validateStatePlayback(state.playback);
      if (reason) return `sprites[${i}].states[${j}].${reason}`;
    }
  }
  return null;
}

/**
 * A stored `sheet` (a MySQL JSON column comes back as a string) as a
 * `SheetCut`, or undefined when it is absent or does not validate.
 */
export function parseSheetCut(raw: unknown): SheetCut | undefined {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  if (value === null || value === undefined) return undefined;
  return validateSheetCut(value) === null ? (value as SheetCut) : undefined;
}

const frameIndex = (cut: SheetCut, index: number) =>
  clampInt(Number.isFinite(index) ? index : 0, 0, Math.max(0, finiteCount(cut) - 1));
const finiteCount = (cut: SheetCut) => (isInt(cut.frameCount) && cut.frameCount > 0 ? cut.frameCount : 1);

/**
 * The source rectangle of frame `index` (clamped into the cut): the JSON rect
 * when `cutBy` is 'json'; otherwise cell `index` read left to right, then top
 * to bottom, of `floor(natural.width / columns) × floor(natural.height / rows)`.
 * The cell size comes from the image itself, so a re-export at another
 * resolution keeps working.
 */
export function sheetFrameRect(cut: SheetCut, index: number, natural: SpriteSize): SheetRect {
  const i = frameIndex(cut, index);
  const rect = cut.cutBy === 'json' ? cut.json?.frames?.[i] : undefined;
  if (rect) return { x: rect.x, y: rect.y, w: rect.w, h: rect.h };
  const columns = Math.max(1, cut.columns || 1);
  const rows = Math.max(1, cut.rows || 1);
  const w = Math.floor((natural.width || 0) / columns);
  const h = Math.floor((natural.height || 0) / rows);
  return { x: (i % columns) * w, y: Math.floor(i / columns) * h, w, h };
}

/** The size of a sheet's frame (frame 0), the size it sizes a sprite by; the natural size without a cut. */
export function sheetFrameSize(cut: SheetCut | undefined, natural: SpriteSize): SpriteSize {
  if (!cut) return natural;
  const r = sheetFrameRect(cut, 0, natural);
  return { width: r.w, height: r.h };
}

const speedOf = (speed: number | undefined) =>
  (isNum(speed) && speed > 0 ? Math.min(PLAYBACK_SPEED_MAX, Math.max(PLAYBACK_SPEED_MIN, speed)) : 1);

/**
 * Each frame's duration, ms, at `speed` (absent = 1): `json.frameMs[i]` when
 * `cutBy` is 'json' and `frameMs` is present, else `1000 / fps`; each
 * divided by `speed`.
 */
export function frameDurations(cut: SheetCut, speed?: number): number[] {
  const k = speedOf(speed);
  const n = finiteCount(cut);
  const fps = isNum(cut.fps) && cut.fps > 0 ? cut.fps : 12;
  const perFrame = cut.cutBy === 'json' && Array.isArray(cut.json?.frameMs) && cut.json!.frameMs!.length >= n
    ? cut.json!.frameMs!
    : null;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const ms = perFrame && isNum(perFrame[i]) && perFrame[i] > 0 ? perFrame[i] : 1000 / fps;
    out.push(ms / k);
  }
  return out;
}

/** A 32-bit FNV-1a hash of a string (the per-sprite random start, D-5). */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

interface SheetPlan {
  first: number;
  last: number;
  /** Frame indexes of one cycle, in play order. */
  sequence: number[];
  /** Cumulative end time of each step of the cycle, ms. */
  ends: number[];
  cycleMs: number;
  /** Cycles before `done`: 1 for `once`, Infinity for forever. */
  cycles: number;
  /** The random-start offset into the cycle, ms. */
  offsetMs: number;
  still: boolean;
}

function plan(cut: SheetCut, playback: StatePlayback | undefined, spriteId: string): SheetPlan {
  const p = playback ?? DEFAULT_PLAYBACK;
  const n = finiteCount(cut);
  let first = 0;
  let last = n - 1;
  if (Array.isArray(p.range) && p.range.length === 2) {
    first = clampInt(Number(p.range[0]) || 0, 0, n - 1);
    last = clampInt(Number(p.range[1]) || 0, 0, n - 1);
    if (last < first) last = first;
  }
  const mode = MODES.includes(p.mode) ? p.mode : 'loop';
  const forward: number[] = [];
  for (let i = first; i <= last; i++) forward.push(i);
  const sequence = mode === 'pingpong' ? [...forward, ...forward.slice(1, -1).reverse()] : forward;
  const durations = frameDurations(cut, p.speed);
  const ends: number[] = [];
  let sum = 0;
  for (const i of sequence) {
    sum += durations[i];
    ends.push(sum);
  }
  // A stale `loops` (stored before D-7) is ignored: loop and pingpong play forever.
  const cycles = mode === 'once' ? 1 : Infinity;
  let offsetMs = 0;
  if (p.randomStart && mode !== 'still') {
    const k = hashString(String(spriteId ?? '')) % forward.length;
    offsetMs = k > 0 ? ends[k - 1] : 0;
  }
  return { first, last, sequence, ends, cycleMs: sum, cycles, offsetMs, still: mode === 'still' || (forward.length === 1 && cycles === Infinity) };
}

const EPS = 1e-6;

/**
 * The frame of a sheet `elapsedMs` after its state began: `{ index, done }`.
 * Frames played are `range` (clamped into the cut) or all; `loop` repeats
 * them, `pingpong` plays first … last … first + 1 and repeats, `once` is one
 * cycle of `loop`, `still` is always the range's first frame and never done.
 * After `once`'s cycle `done` is true and the frame is the range's last
 * (`hold-last`, also the default and whatever else a stale `whenDone` holds)
 * or first (`hold-first`). `randomStart` offsets the time by the start of frame
 * `hashString(spriteId) mod frames-in-range` (D-5). Depends on its arguments
 * only, so seeking is exact.
 */
export function sheetFrameAt(
  cut: SheetCut,
  playback: StatePlayback | undefined,
  elapsedMs: number,
  spriteId: string,
): { index: number; done: boolean } {
  const pl = plan(cut, playback, spriteId);
  if (pl.still || !(pl.cycleMs > 0)) return { index: pl.first, done: false };
  const t = Math.max(0, Number.isFinite(elapsedMs) ? elapsedMs : 0) + pl.offsetMs;
  if (t + EPS >= pl.cycles * pl.cycleMs) {
    const holdFirst = (playback ?? DEFAULT_PLAYBACK).whenDone === 'hold-first';
    return { index: holdFirst ? pl.first : pl.last, done: true };
  }
  let tc = t - Math.floor((t + EPS) / pl.cycleMs) * pl.cycleMs;
  if (tc < 0) tc = 0;
  for (let i = 0; i < pl.sequence.length; i++) {
    if (tc + EPS < pl.ends[i]) return { index: pl.sequence[i], done: false };
  }
  return { index: pl.sequence[pl.sequence.length - 1], done: false };
}

/**
 * How long after its state began a sheet is `done` (ms): one cycle for
 * `once`, Infinity for every other mode. A helper for whoever describes or
 * times a state; `evaluateFrame` does not need it.
 */
export function sheetPlayLength(cut: SheetCut, playback: StatePlayback | undefined, spriteId: string): number {
  const pl = plan(cut, playback, spriteId);
  if (pl.still || !(pl.cycleMs > 0) || !Number.isFinite(pl.cycles)) return Infinity;
  return Math.max(0, pl.cycles * pl.cycleMs - pl.offsetMs);
}
